import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import listasService from '../../services/listasProveedor.service';
import proveedoresService from '../../services/proveedores.service';

const ETIQUETA = {
  conocido: 'Conocido',
  ambiguo: 'Ambiguo',
  sugerido: 'Sugerido',
  desconocido: 'Desconocido',
  rechazado: 'Rechazado',
  familia_distinta: 'Otra calidad',
  error: 'Error'
};

const CAMPOS = [
  ['codigo_externo', 'Código del proveedor'],
  ['nombre', 'Nombre'],
  ['precio', 'Precio de lista'],
  ['unidad', 'Unidad'],
  ['presentacion', 'Presentación'],
  ['calidad', 'Calidad'],
  ['marca', 'Marca']
];

const campo = 'nexo-field';

function aBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

export default function ListasProveedor() {
  const { hasPermission } = useAuth();
  const puedeGuardar = typeof hasPermission === 'function' && hasPermission('productos', 'editar') === true;
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [errorCarga, setErrorCarga] = useState('');
  const [avisoProveedores, setAvisoProveedores] = useState('');
  const [errorArchivo, setErrorArchivo] = useState('');
  const [proveedores, setProveedores] = useState([]);
  const [versiones, setVersiones] = useState([]);
  const [proveedorId, setProveedorId] = useState('');
  const [archivo, setArchivo] = useState(null);
  const [mapeo, setMapeo] = useState({});
  const [preview, setPreview] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setErrorCarga('');
    setAvisoProveedores('');
    const [listasRes, provRes] = await Promise.allSettled([
      listasService.listar(),
      proveedoresService.obtenerTodos()
    ]);
    if (listasRes.status === 'fulfilled') {
      setVersiones(listasRes.value);
    } else {
      setVersiones([]);
      const mensaje = listasRes.reason?.message || 'No se pudieron leer las listas';
      setErrorCarga(mensaje);
      toast.error(mensaje);
    }
    if (provRes.status === 'fulfilled') {
      setProveedores(Array.isArray(provRes.value) ? provRes.value : []);
    } else {
      setProveedores([]);
      setAvisoProveedores(provRes.reason?.message || 'No se pudieron leer los proveedores');
    }
    setCargando(false);
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const previsualizar = async (siguienteArchivo, siguienteMapeo) => {
    setErrorArchivo('');
    setPreview(null);
    if (!proveedorId) {
      setErrorArchivo('Elegí un proveedor antes de leer el archivo.');
      return;
    }
    try {
      const data = await listasService.previsualizar({
        proveedor_id: proveedorId,
        nombre_archivo: siguienteArchivo.nombre,
        contenido_base64: siguienteArchivo.contenido_base64,
        mapeo: siguienteMapeo
      });
      setPreview(data);
      setMapeo(data?.mapeo || {});
    } catch (error) {
      setErrorArchivo(error.message || 'No se pudo leer el archivo');
      toast.error(error.message || 'No se pudo leer el archivo');
    }
  };

  const elegirArchivo = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const contenido_base64 = aBase64(await file.arrayBuffer());
    const siguiente = { nombre: file.name, contenido_base64 };
    setArchivo(siguiente);
    setMapeo({});
    await previsualizar(siguiente, undefined);
  };

  const corregirMapeo = async (nombreCampo, valor) => {
    const siguiente = { ...mapeo, [nombreCampo]: valor || null };
    setMapeo(siguiente);
    if (archivo) await previsualizar(archivo, siguiente);
  };

  const guardar = async () => {
    if (!archivo || !proveedorId) return;
    setGuardando(true);
    try {
      const respuesta = await listasService.guardar({
        proveedor_id: proveedorId,
        nombre_archivo: archivo.nombre,
        contenido_base64: archivo.contenido_base64,
        mapeo
      });
      toast.success(respuesta.message || 'Lista guardada');
      await cargar();
    } catch (error) {
      toast.error(error.message || 'No se pudo guardar la lista');
      if (error.code === 'ARCHIVO_DUPLICADO') await cargar();
    } finally {
      setGuardando(false);
    }
  };

  const visibles = versiones.filter((version) => !proveedorId || version.proveedor_id === proveedorId);

  if (cargando) {
    return <div className="flex justify-center py-16"><Spinner /></div>;
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-1">
      <div>
        <h1 className="text-2xl font-bold text-e0e3-foreground">Listas de proveedor</h1>
        <p className="mt-1 text-sm text-e0e3-muted">
          Importá un Excel o CSV del proveedor. Guardar una versión no cambia productos, costos, stock ni compras.
        </p>
      </div>

      {errorCarga && <p className="text-sm text-red-300">{errorCarga}</p>}
      {avisoProveedores && (
        <p className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-e0e3-muted">
          {avisoProveedores}
        </p>
      )}

      <section className="space-y-3 rounded-2xl border border-white/10 bg-e0e3-surface p-4">
        <label className="text-sm text-e0e3-muted">
          Proveedor
          <select className={campo} value={proveedorId} onChange={(event) => setProveedorId(event.target.value)}>
            <option value="">Elegí un proveedor</option>
            {proveedores.map((prov) => (
              <option key={prov.id} value={prov.id}>{prov.nombre}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm text-e0e3-muted">
          Archivo XLSX o CSV
          <input className="mt-1 block text-sm text-e0e3-foreground" type="file" accept=".xlsx,.csv,text/csv" onChange={elegirArchivo} />
        </label>
        {errorArchivo && <p className="text-sm text-red-300">{errorArchivo}</p>}
        {preview && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              {CAMPOS.map(([clave, etiqueta]) => (
                <label key={clave} className="text-sm text-e0e3-muted">
                  {etiqueta}
                  <select className={campo} value={mapeo[clave] || ''} onChange={(event) => corregirMapeo(clave, event.target.value)}>
                    <option value="">Sin columna</option>
                    {(preview.encabezados || []).map((encabezado) => (
                      <option key={`${clave}-${encabezado}`} value={encabezado}>{encabezado}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <p className="text-sm text-e0e3-muted">
              {preview.resumen?.total || 0} filas · conocidas {preview.resumen?.conocidas || 0} · ambiguas {preview.resumen?.ambiguas || 0} · rechazadas {preview.resumen?.rechazadas || 0} · desconocidas {preview.resumen?.desconocidas || 0} · errores {preview.resumen?.errores || 0}
            </p>
            <p className="text-sm text-e0e3-muted">
              Origen {preview.evidencia?.sha256 ? preview.evidencia.sha256.slice(0, 12) : '—'} · archivo {preview.evidencia?.archivo_conservado ? 'conservado' : 'solo hash'}
            </p>
            <div className="space-y-2">
              {(preview.filas || []).map((fila) => (
                <article key={`${fila.fila}-${fila.codigo_externo}`} className="rounded-xl border border-white/10 px-3 py-2 text-sm">
                  <p className="text-e0e3-foreground">Fila {fila.fila}: {fila.codigo_externo || 'sin código'} — {fila.nombre || 'sin nombre'}</p>
                  <p className="text-e0e3-muted">
                    {ETIQUETA[fila.estado_fila] || fila.estado_fila}
                    {fila.producto_nombre ? ` · ${fila.producto_nombre}` : ''}
                    {fila.precio != null ? ` · $${fila.precio}` : ''}
                    {fila.errores?.length ? ` · ${fila.errores.join(', ')}` : ''}
                    {fila.reutiliza === 'rechazada' ? ' · decisión rechazada, no se propone de nuevo' : ''}
                  </p>
                  {fila.candidatos?.length > 0 && (
                    <p className="text-e0e3-muted">Candidatos: {fila.candidatos.map((cand) => cand.nombre).join(', ')}</p>
                  )}
                </article>
              ))}
            </div>
            {puedeGuardar && (
              <Button type="button" color="primary" disabled={guardando} onClick={guardar}>Guardar versión</Button>
            )}
          </>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-base font-semibold text-e0e3-foreground">Versiones</h2>
        {visibles.length === 0 && <p className="text-sm text-e0e3-muted">Todavía no hay listas guardadas.</p>}
        {visibles.map((version) => (
          <article key={version.id} className="rounded-2xl border border-white/10 bg-e0e3-surface p-4 text-sm">
            <p className="font-medium text-e0e3-foreground">Versión {version.version} · {version.nombre_archivo}</p>
            <p className="text-e0e3-muted">
              {version.usuario_nombre || '—'} · {version.creado_en ? String(version.creado_en).slice(0, 16).replace('T', ' ') : '—'}
            </p>
            <p className="text-e0e3-muted">
              {version.resumen?.total || 0} filas · hash {version.hash ? version.hash.slice(0, 12) : '—'} · archivo {version.evidencia?.archivo_conservado ? 'conservado' : 'solo hash'}
            </p>
          </article>
        ))}
      </section>
    </div>
  );
}
